import {
  buildDashboardItems,
  type DynamicGridItem,
  type DynamicGridItemMeta,
  type DynamicGridPosition,
} from '@cast/design-system';

import {
  WORKLOAD_CATEGORIES,
  WORKLOAD_CATEGORY_FILTER_OPTIONS,
} from '../../_constants';
import type { WorkloadCategory } from '../../_constants';

export enum WorkloadDashboardItemId {
  CpuCost = 'workload-cpu-cost',
  MemoryCost = 'workload-memory-cost',
  GpuCost = 'workload-gpu-cost',
  StorageCost = 'workload-storage-cost',
  ComputeSpend = 'workload-compute-spend',
  CpuAutoscaling = 'workload-cpu-autoscaling',
  MemoryAutoscaling = 'workload-memory-autoscaling',
  OomKills = 'workload-oom-kills',
  CpuPrediction = 'workload-cpu-prediction',
  JvmHeap = 'workload-jvm-heap',
  HpaCharts = 'workload-hpa-charts',
  GpuUtilization = 'workload-gpu-utilization',
  GpuUtilizationBreakdown = 'workload-gpu-utilization-breakdown',
  GpuMemory = 'workload-gpu-memory',
  GpuMemoryWaste = 'workload-gpu-memory-waste',
  GpuCount = 'workload-gpu-count',
  GpuWaste = 'workload-gpu-waste',
  ReliabilityLatency = 'workload-reliability-latency',
  ReliabilityTraffic = 'workload-reliability-traffic',
  NetworkCost = 'workload-network-cost',
  NetworkTraffic = 'workload-network-traffic',
  NetworkTrafficBreakdown = 'workload-network-traffic-breakdown',
  CrossAzCommunicationCosts = 'workload-cross-az-communication-costs',
  CrossAzCommunicationTraffic = 'workload-cross-az-communication-traffic',
}

export type WorkloadDashboardItem = DynamicGridItem & {
  id: WorkloadDashboardItemId;
};

type WorkloadDashboardCategory = Exclude<
  WorkloadCategory,
  typeof WORKLOAD_CATEGORIES.SAVINGS
>;

const WORKLOAD_DASHBOARD_ITEM_META: Record<
  WorkloadDashboardItemId,
  DynamicGridItemMeta & { id: WorkloadDashboardItemId }
> = {
  [WorkloadDashboardItemId.CpuCost]: {
    id: WorkloadDashboardItemId.CpuCost,
    title: 'CPU cost',
    description: 'Cost per requested vCPU over time',
    w: 6,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.MemoryCost]: {
    id: WorkloadDashboardItemId.MemoryCost,
    title: 'Memory cost',
    description: 'Cost per requested GB over time',
    w: 6,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.GpuCost]: {
    id: WorkloadDashboardItemId.GpuCost,
    title: 'GPU cost',
    description: 'Cost per requested GPU over time',
    w: 6,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.StorageCost]: {
    id: WorkloadDashboardItemId.StorageCost,
    title: 'Storage cost',
    description: 'Cost per requested GB over time',
    w: 6,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.ComputeSpend]: {
    id: WorkloadDashboardItemId.ComputeSpend,
    title: 'Compute spend',
    description: 'Compute spend breakdown over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.CpuAutoscaling]: {
    id: WorkloadDashboardItemId.CpuAutoscaling,
    title: 'CPU autoscaling',
    description: 'CPU usage and right-sizing recommendations',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.MemoryAutoscaling]: {
    id: WorkloadDashboardItemId.MemoryAutoscaling,
    title: 'Memory autoscaling',
    description: 'Memory usage and right-sizing recommendations',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.OomKills]: {
    id: WorkloadDashboardItemId.OomKills,
    title: 'OOM kills',
    description: 'OOM kill events over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.CpuPrediction]: {
    id: WorkloadDashboardItemId.CpuPrediction,
    title: 'Predictive autoscaling: CPU',
    description: 'CPU usage with predictive scaling recommendations',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.JvmHeap]: {
    id: WorkloadDashboardItemId.JvmHeap,
    title: 'JVM heap',
    description: 'JVM heap and non-heap memory usage',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.HpaCharts]: {
    id: WorkloadDashboardItemId.HpaCharts,
    title: 'Horizontal autoscaling',
    description: 'Replica count and scaling events over time',
    w: 12,
    h: 5,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.GpuUtilization]: {
    id: WorkloadDashboardItemId.GpuUtilization,
    title: 'GPU utilization',
    description: 'GPU utilization over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.GpuUtilizationBreakdown]: {
    id: WorkloadDashboardItemId.GpuUtilizationBreakdown,
    title: 'GPU utilization',
    description: 'Average GPU utilization and GPU memory utilization over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.GpuMemory]: {
    id: WorkloadDashboardItemId.GpuMemory,
    title: 'GPU memory',
    description: 'GPU memory usage over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.GpuMemoryWaste]: {
    id: WorkloadDashboardItemId.GpuMemoryWaste,
    title: 'GPU Memory waste',
    description: 'GPU memory waste over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.GpuCount]: {
    id: WorkloadDashboardItemId.GpuCount,
    title: 'GPU count',
    description: 'GPU instance count over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.GpuWaste]: {
    id: WorkloadDashboardItemId.GpuWaste,
    title: 'GPU waste',
    description: 'GPU waste over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.ReliabilityLatency]: {
    id: WorkloadDashboardItemId.ReliabilityLatency,
    title: 'Reliability latency',
    description: 'Request latency over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.ReliabilityTraffic]: {
    id: WorkloadDashboardItemId.ReliabilityTraffic,
    title: 'Reliability traffic',
    description: 'Request traffic over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.NetworkCost]: {
    id: WorkloadDashboardItemId.NetworkCost,
    title: 'Network cost',
    description: 'Network cost breakdown over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.NetworkTraffic]: {
    id: WorkloadDashboardItemId.NetworkTraffic,
    title: 'Network traffic',
    description: 'Network traffic over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.NetworkTrafficBreakdown]: {
    id: WorkloadDashboardItemId.NetworkTrafficBreakdown,
    title: 'Traffic breakdown',
    description: 'Network traffic breakdown over time',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.CrossAzCommunicationCosts]: {
    id: WorkloadDashboardItemId.CrossAzCommunicationCosts,
    title: 'Cross-AZ communication costs',
    description: 'Cross-AZ communication costs and highest to lowest list',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
  [WorkloadDashboardItemId.CrossAzCommunicationTraffic]: {
    id: WorkloadDashboardItemId.CrossAzCommunicationTraffic,
    title: 'Cross-AZ communication traffic',
    description: 'Cross-AZ communication traffic and highest to lowest list',
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
  },
};

const ALL_WORKLOAD_DASHBOARD_ITEM_IDS = Object.keys(
  WORKLOAD_DASHBOARD_ITEM_META
) as WorkloadDashboardItemId[];

/**
 * Per-category default positions from Phase-1 Figma.
 * Gaps reserve space for unimplemented cards (e.g. total usage across pods).
 * Pair with DynamicGrid `compactor="none"`.
 */
const WORKLOAD_DASHBOARD_LAYOUTS: Record<
  WorkloadDashboardCategory,
  Partial<Record<WorkloadDashboardItemId, DynamicGridPosition>>
> = {
  [WORKLOAD_CATEGORIES.DEFAULT]: {
    [WorkloadDashboardItemId.CpuAutoscaling]: { x: 0, y: 0, w: 12, h: 4 },
    [WorkloadDashboardItemId.MemoryAutoscaling]: { x: 0, y: 4, w: 12, h: 4 },
    [WorkloadDashboardItemId.HpaCharts]: { x: 0, y: 8, w: 12, h: 5 },
    [WorkloadDashboardItemId.ReliabilityTraffic]: { x: 0, y: 13, w: 6, h: 4 },
    [WorkloadDashboardItemId.ReliabilityLatency]: { x: 6, y: 13, w: 6, h: 4 },
    [WorkloadDashboardItemId.ComputeSpend]: { x: 0, y: 17, w: 12, h: 4 },
  },
  [WORKLOAD_CATEGORIES.AUTOSCALING]: {
    [WorkloadDashboardItemId.CpuAutoscaling]: { x: 0, y: 0, w: 12, h: 4 },
    [WorkloadDashboardItemId.CpuPrediction]: { x: 0, y: 4, w: 12, h: 4 },
    [WorkloadDashboardItemId.MemoryAutoscaling]: { x: 0, y: 8, w: 12, h: 4 },
    [WorkloadDashboardItemId.JvmHeap]: { x: 0, y: 12, w: 12, h: 4 },
    [WorkloadDashboardItemId.HpaCharts]: { x: 0, y: 16, w: 12, h: 5 },
    [WorkloadDashboardItemId.GpuUtilization]: { x: 0, y: 21, w: 6, h: 4 },
    [WorkloadDashboardItemId.GpuMemory]: { x: 6, y: 21, w: 6, h: 4 },
    [WorkloadDashboardItemId.GpuCount]: { x: 0, y: 25, w: 12, h: 4 },
  },
  // Compute spend on top, then 2x2 cost cards (Phase-1 compute frame).
  [WORKLOAD_CATEGORIES.COMPUTE]: {
    [WorkloadDashboardItemId.ComputeSpend]: { x: 0, y: 0, w: 12, h: 4 },
    [WorkloadDashboardItemId.CpuCost]: { x: 0, y: 4, w: 6, h: 4 },
    [WorkloadDashboardItemId.MemoryCost]: { x: 6, y: 4, w: 6, h: 4 },
    [WorkloadDashboardItemId.GpuCost]: { x: 0, y: 8, w: 6, h: 4 },
    [WorkloadDashboardItemId.StorageCost]: { x: 6, y: 8, w: 6, h: 4 },
  },
  [WORKLOAD_CATEGORIES.RELIABILITY]: {
    [WorkloadDashboardItemId.ReliabilityTraffic]: { x: 0, y: 0, w: 6, h: 4 },
    [WorkloadDashboardItemId.ReliabilityLatency]: { x: 6, y: 0, w: 6, h: 4 },
  },
  [WORKLOAD_CATEGORIES.NETWORK]: {
    [WorkloadDashboardItemId.NetworkCost]: { x: 0, y: 0, w: 6, h: 4 },
    [WorkloadDashboardItemId.NetworkTraffic]: { x: 6, y: 0, w: 6, h: 4 },
    [WorkloadDashboardItemId.NetworkTrafficBreakdown]: {
      x: 0,
      y: 4,
      w: 12,
      h: 4,
    },
    [WorkloadDashboardItemId.CrossAzCommunicationCosts]: {
      x: 0,
      y: 8,
      w: 12,
      h: 4,
    },
    [WorkloadDashboardItemId.CrossAzCommunicationTraffic]: {
      x: 0,
      y: 12,
      w: 12,
      h: 4,
    },
  },
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: {
    [WorkloadDashboardItemId.GpuUtilizationBreakdown]: {
      x: 0,
      y: 0,
      w: 12,
      h: 4,
    },
    [WorkloadDashboardItemId.GpuMemoryWaste]: { x: 0, y: 4, w: 12, h: 4 },
    [WorkloadDashboardItemId.GpuWaste]: { x: 0, y: 8, w: 12, h: 4 },
  },
};

const buildWorkloadDashboardItems = (
  positions: Partial<Record<WorkloadDashboardItemId, DynamicGridPosition>>
): WorkloadDashboardItem[] =>
  buildDashboardItems(
    ALL_WORKLOAD_DASHBOARD_ITEM_IDS.map(
      (id) => WORKLOAD_DASHBOARD_ITEM_META[id]
    ),
    positions
  );

export const WORKLOAD_DASHBOARD_ITEMS_BY_CATEGORY: Record<
  WorkloadDashboardCategory,
  WorkloadDashboardItem[]
> = {
  [WORKLOAD_CATEGORIES.DEFAULT]: buildWorkloadDashboardItems(
    WORKLOAD_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.DEFAULT]
  ),
  [WORKLOAD_CATEGORIES.AUTOSCALING]: buildWorkloadDashboardItems(
    WORKLOAD_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.AUTOSCALING]
  ),
  [WORKLOAD_CATEGORIES.COMPUTE]: buildWorkloadDashboardItems(
    WORKLOAD_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.COMPUTE]
  ),
  [WORKLOAD_CATEGORIES.RELIABILITY]: buildWorkloadDashboardItems(
    WORKLOAD_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.RELIABILITY]
  ),
  [WORKLOAD_CATEGORIES.NETWORK]: buildWorkloadDashboardItems(
    WORKLOAD_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.NETWORK]
  ),
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: buildWorkloadDashboardItems(
    WORKLOAD_DASHBOARD_LAYOUTS[WORKLOAD_CATEGORIES.GPU_UTILIZATION]
  ),
};

export const WORKLOAD_DASHBOARD_DEFAULT_VISIBLE: Record<
  WorkloadDashboardCategory,
  WorkloadDashboardItemId[]
> = {
  // Default view charts per Phase-1 handoff (autoscaling + reliability + spend).
  [WORKLOAD_CATEGORIES.DEFAULT]: [
    WorkloadDashboardItemId.CpuAutoscaling,
    WorkloadDashboardItemId.MemoryAutoscaling,
    WorkloadDashboardItemId.HpaCharts,
    WorkloadDashboardItemId.ReliabilityTraffic,
    WorkloadDashboardItemId.ReliabilityLatency,
    WorkloadDashboardItemId.ComputeSpend,
  ],
  [WORKLOAD_CATEGORIES.AUTOSCALING]: [
    WorkloadDashboardItemId.CpuAutoscaling,
    WorkloadDashboardItemId.CpuPrediction,
    WorkloadDashboardItemId.MemoryAutoscaling,
    WorkloadDashboardItemId.JvmHeap,
    WorkloadDashboardItemId.HpaCharts,
    WorkloadDashboardItemId.GpuUtilization,
    WorkloadDashboardItemId.GpuMemory,
    WorkloadDashboardItemId.GpuCount,
  ],
  [WORKLOAD_CATEGORIES.COMPUTE]: [
    WorkloadDashboardItemId.ComputeSpend,
    WorkloadDashboardItemId.CpuCost,
    WorkloadDashboardItemId.MemoryCost,
    WorkloadDashboardItemId.GpuCost,
    WorkloadDashboardItemId.StorageCost,
  ],
  [WORKLOAD_CATEGORIES.RELIABILITY]: [
    WorkloadDashboardItemId.ReliabilityTraffic,
    WorkloadDashboardItemId.ReliabilityLatency,
  ],
  [WORKLOAD_CATEGORIES.NETWORK]: [
    WorkloadDashboardItemId.NetworkCost,
    WorkloadDashboardItemId.NetworkTraffic,
    WorkloadDashboardItemId.NetworkTrafficBreakdown,
    WorkloadDashboardItemId.CrossAzCommunicationCosts,
    WorkloadDashboardItemId.CrossAzCommunicationTraffic,
  ],
  [WORKLOAD_CATEGORIES.GPU_UTILIZATION]: [
    WorkloadDashboardItemId.GpuUtilizationBreakdown,
    WorkloadDashboardItemId.GpuMemoryWaste,
    WorkloadDashboardItemId.GpuWaste,
  ],
};

/** Primary category per card (folder ownership) for customize-drawer grouping/filter. */
export const WORKLOAD_DASHBOARD_ITEM_CATEGORY: Record<
  WorkloadDashboardItemId,
  Exclude<WorkloadCategory, typeof WORKLOAD_CATEGORIES.DEFAULT>
> = {
  [WorkloadDashboardItemId.CpuCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.MemoryCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.GpuCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.StorageCost]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.ComputeSpend]: WORKLOAD_CATEGORIES.COMPUTE,
  [WorkloadDashboardItemId.CpuAutoscaling]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.MemoryAutoscaling]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.OomKills]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.CpuPrediction]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.JvmHeap]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.HpaCharts]: WORKLOAD_CATEGORIES.AUTOSCALING,
  [WorkloadDashboardItemId.GpuUtilization]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuUtilizationBreakdown]:
    WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuMemory]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuMemoryWaste]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuCount]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.GpuWaste]: WORKLOAD_CATEGORIES.GPU_UTILIZATION,
  [WorkloadDashboardItemId.ReliabilityLatency]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadDashboardItemId.ReliabilityTraffic]: WORKLOAD_CATEGORIES.RELIABILITY,
  [WorkloadDashboardItemId.NetworkCost]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.NetworkTraffic]: WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.NetworkTrafficBreakdown]:
    WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.CrossAzCommunicationCosts]:
    WORKLOAD_CATEGORIES.NETWORK,
  [WorkloadDashboardItemId.CrossAzCommunicationTraffic]:
    WORKLOAD_CATEGORIES.NETWORK,
};

export const WORKLOAD_DRAWER_CATEGORY_OPTIONS =
  WORKLOAD_CATEGORY_FILTER_OPTIONS.filter(
    ({ value }) => value !== WORKLOAD_CATEGORIES.SAVINGS
  );

export const getWorkloadDashboardStorageKey = (category: string) =>
  `workload-dashboard-${category}-v17`;
